import { useLoaderData, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMemo, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { Json, Owner, Status } from '../models/json';
import BmwMainForm from './BmwMainForm';
import { DocumentData } from '../../../iveco/recall-campaign/vehicle/models/document-data';
import agent from '../../../../api/agent';
import { Static } from '../../../../static';
import { useStore } from '../../../../context/store';

type FormValues = {
	statusKey: string;
	email: string;
	newOwnerSurname: string;
	newOwnerName: string;
	newOwnerAddress: string;
	newOwnerStreetNumber: string;
	newOwnerCap: string;
	newOwnerCity: string;
	newOwnerProvince: string;
	newOwnerState: string;
	complaintDate: string;
	other: string;
};

const BmwMain = () => {
	const [baseJson, setBaseJson] = useState(useLoaderData() as Json);
	const [json, setJson] = useState<Json>(JSON.parse(JSON.stringify(baseJson)));
	const vehicle = baseJson?.Vehicles[0] ?? null;
	const modifiedVehicle = !!baseJson?.Vehicles?.[0]?.Status?.Key;

	const { loaderStore } = useStore();
	const { id } = useParams();

	const initialValues = useMemo<FormValues>(
		() => ({
			statusKey: baseJson.Vehicles?.[0].Status?.Key
				? String(baseJson.Vehicles[0].Status.Key)
				: '',
			email: baseJson?.Holder?.Email ?? '',
			newOwnerSurname: baseJson.Vehicles?.[0].NewOwner?.Surname ?? '',
			newOwnerName: baseJson.Vehicles?.[0].NewOwner?.Name ?? '',
			newOwnerAddress: baseJson.Vehicles?.[0].NewOwner?.Address ?? '',
			newOwnerStreetNumber: baseJson.Vehicles?.[0].NewOwner?.StreetNumber ?? '',
			newOwnerCap: baseJson.Vehicles?.[0].NewOwner?.Cap ?? '',
			newOwnerCity: baseJson.Vehicles?.[0].NewOwner?.City ?? '',
			newOwnerProvince: baseJson.Vehicles?.[0].NewOwner?.Province ?? '',
			newOwnerState: baseJson.Vehicles?.[0].NewOwner?.State ?? '',
			complaintDate: baseJson.Vehicles?.[0].ComplaintDate ?? '',
			other: baseJson.Vehicles?.[0].Other ?? '',
		}),
		[baseJson],
	);

	const validationSchema = Yup.object({
		statusKey: Yup.string()
			.required("Seleziona un'opzione")
			.oneOf(['1', '2', '3', '4'], "Seleziona un'opzione"),

		email: Yup.string()
			.trim()
			.email('Email non valida')
			.required('Email obbligatoria'),

		newOwnerSurname: Yup.string().when('statusKey', {
			is: '1',
			then: (s) => s.trim().required('Cognome obbligatorio'),
			otherwise: (s) => s.trim().notRequired(),
		}),
		newOwnerName: Yup.string().when('statusKey', {
			is: '1',
			then: (s) => s.trim().required('Nome obbligatorio'),
			otherwise: (s) => s.trim().notRequired(),
		}),
		newOwnerAddress: Yup.string().when('statusKey', {
			is: '1',
			then: (s) => s.trim().required('Indirizzo obbligatorio'),
			otherwise: (s) => s.trim().notRequired(),
		}),
		newOwnerStreetNumber: Yup.string().when('statusKey', {
			is: '1',
			then: (s) => s.trim().required('Numero civico obbligatorio'),
			otherwise: (s) => s.trim().notRequired(),
		}),
		newOwnerCap: Yup.string().when('statusKey', {
			is: '1',
			then: (s) =>
				s
					.trim()
					.matches(/^\d{5}$/, 'CAP non valido')
					.required('CAP obbligatorio'),
			otherwise: (s) => s.trim().notRequired(),
		}),
		newOwnerCity: Yup.string().when('statusKey', {
			is: '1',
			then: (s) => s.trim().required('Città obbligatoria'),
			otherwise: (s) => s.trim().notRequired(),
		}),
		newOwnerProvince: Yup.string().when('statusKey', {
			is: '1',
			then: (s) => s.trim().required('Provincia obbligatoria'),
			otherwise: (s) => s.trim().notRequired(),
		}),
		newOwnerState: Yup.string().when('statusKey', {
			is: '1',
			then: (s) => s.trim().required('Stato obbligatorio'),
			otherwise: (s) => s.trim().notRequired(),
		}),

		complaintDate: Yup.string().when('statusKey', {
			is: '2',
			then: (s) => s.required('Data denuncia obbligatoria'),
			otherwise: (s) => s.notRequired(),
		}),

		other: Yup.string().when('statusKey', {
			is: '4',
			then: (s) => s.trim().required('Specificare cosa è successo'),
			otherwise: (s) => s.trim().notRequired(),
		}),
	});

	const submit = async (
		values: FormValues,
		actions: FormikHelpers<FormValues>,
	) => {
		try {
			if (!baseJson?.Vehicles?.length) {
				return;
			}

			if (!id) {
				toast.error(Static.saveFailed);
				return;
			}

			const statusKey = Number(values.statusKey);

			if (!statusKey) {
				toast.error("Seleziona un'opzione");
				return;
			}
			const statusValue = json.StatusOptions.find(
				(x) => x.Key === statusKey,
			)?.Value;

			const vehicles = [...json.Vehicles];
			const v = { ...vehicles[0] };

			if (!v.Status) v.Status = new Status();
			v.Status.Key = statusKey;
			v.Status.Value = statusValue;

			loaderStore.setAppLoadingTrue();

			const holder = { ...(json.Holder ?? new Owner()) };
			holder.Email = values.email.trim();

			v.NewOwner = null;
			v.ComplaintDate = '';
			v.Other = '';

			if (statusKey === 1) {
				const newOwner = new Owner();
				newOwner.Surname = values.newOwnerSurname?.trim();
				newOwner.Name = values.newOwnerName?.trim();
				newOwner.Address = values.newOwnerAddress?.trim();
				newOwner.StreetNumber = values.newOwnerStreetNumber?.trim();
				newOwner.Cap = values.newOwnerCap?.trim();
				newOwner.City = values.newOwnerCity?.trim();
				newOwner.Province = values.newOwnerProvince?.trim();
				newOwner.State = values.newOwnerState?.trim();
				newOwner.Email = values.email.trim();

				v.NewOwner = newOwner;
			}

			if (statusKey === 2) {
				v.ComplaintDate = values.complaintDate;
			}

			if (statusKey === 4) {
				v.Other = values.other?.trim();
			}

			vehicles[0] = v;

			const updatedJson: Json = {
				...json,
				Holder: holder,
				Vehicles: vehicles,
			};

			setJson(updatedJson);

			const documentData = new DocumentData();
			documentData.json = JSON.stringify(updatedJson);
			documentData.documentId = Number(id);

			const res = await agent.Document.saveDocumentData(documentData);

			if (res === true) {
				toast.success(Static.thanks);
				setBaseJson(updatedJson);
			} else {
				toast.error(Static.saveFailed);
			}
		} catch (ex) {
			toast.error(Static.anUnexpectedErrorHasOccurred);
		} finally {
			actions.setSubmitting(false);
			loaderStore.setAppLoadingFalse();
		}
	};

	return (
		<div className='bmw-vehicle-main' id='main-document'>
			<div className='main-hero'>
				<img
					className='main-hero__bg'
					src='/images/bmw/recall-campaign/BmwBackground.jpg'
					alt=''
					aria-hidden='true'
				/>

				<div className='main-hero__overlay'>
					<div className='main-hero__top'>
						<img
							className='main-hero__group'
							src='/images/bmw/recall-campaign/logo-bmw-group.svg'
							alt='Logo BMW Group'
						/>

						<div className='main-hero__brands'>
							<img
								className='main-hero__brand'
								src='/images/bmw/recall-campaign/logo-bmw.svg'
								alt='Logo BMW'
							/>
							<img
								className='main-hero__brand'
								src='/images/bmw/recall-campaign/logo-mini.svg'
								alt='Logo Mini'
							/>
						</div>
					</div>

					<div className='main-hero__client'>
						<div className='main-hero__client-label'>Cliente</div>
						<div className='main-hero__client-name'>{baseJson.Dealership}</div>
					</div>
				</div>
			</div>
			{vehicle ? (
				<>
					<section className='main-section'>
						<h3 className='main-section__title'>
							RICHIAMO N°: {baseJson.CampaignNumber}
						</h3>
						<div className='main-section__row'>
							<div className='main-section__fields'>
								<div className='main-field'>
									<div className='main-field__value'>
										Telaio: {vehicle.Chassis}
									</div>
								</div>
								<div className='main-field'>
									<div className='main-field__value'>
										Targa: {vehicle.LicensePlate}
									</div>
								</div>
								{modifiedVehicle && (
									<h5 className='mt-4 text-danger'>
										Questo veicolo è stato dichiarato &quot;
										{vehicle.Status.Value?.toLowerCase()}&quot; e non è più
										modificabile.
									</h5>
								)}
							</div>
						</div>
					</section>
					<section className='main-form'>
						<Formik
							validateOnChange
							enableReinitialize
							initialValues={initialValues}
							validationSchema={validationSchema}
							onSubmit={submit}
						>
							<BmwMainForm
								disabled={modifiedVehicle}
								statusOptions={baseJson.StatusOptions}
							/>
						</Formik>
					</section>
				</>
			) : (
				<section className='main-section'>
					<h3 className='main-section__title'>
						RICHIAMO N°: {baseJson.CampaignNumber}
					</h3>
					<div className='main-section__row'>
						<div className='main-section__fields'>
							<h5 className='mt-4 text-danger'>Veicolo non presente.</h5>
						</div>
					</div>
				</section>
			)}
		</div>
	);
};

export default BmwMain;
